import type { Lang } from '@/types/lang'
import type { TranslationStatus } from '@/features/ai/studioStartTypes'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

export interface StatusBadge {
  label: string
  variant: 'success' | 'warning' | 'neutral'
  /** «Что это значит» — тултип чипа и в меню, и во вкладке «Язык». */
  hint: string
}

/** Подписи и подсказки — дословно из спеки CEO и прототипа
 *  (`translationFreshness.ts`): «если меняется одно, меняется и другое».
 *  Своих формулировок рядом не заводить. Тексты — геттеры: читают язык
 *  интерфейса в момент обращения (`messages.ts`). */
type Msgs = (typeof messages)['ru']
const badge = (
  variant: StatusBadge['variant'],
  label: (m: Msgs) => string,
  hint: (m: Msgs) => string,
): StatusBadge => ({
  variant,
  get label() {
    return label(messagesNow(messages))
  },
  get hint() {
    return hint(messagesNow(messages))
  },
})

export const STATUS_BADGE: Record<TranslationStatus, StatusBadge> = {
  fresh: badge(
    'success',
    (m) => m.statusFreshLabel,
    (m) => m.statusFreshHint,
  ),
  outdated: badge(
    'warning',
    (m) => m.statusOutdatedLabel,
    (m) => m.statusOutdatedHint,
  ),
  partial: badge(
    'neutral',
    (m) => m.statusPartialLabel,
    (m) => m.statusPartialHint,
  ),
  untranslated: badge(
    'neutral',
    (m) => m.statusUntranslatedLabel,
    (m) => m.statusUntranslatedHint,
  ),
}

/** Незнакомый статус (сервер расширил реестр раньше студии) — без чипа, а не
 *  падение меню. Собственный ключ, а не `in`: `'constructor'` иначе нашёлся бы. */
export const statusBadgeOf = (status: string | null | undefined): StatusBadge | null =>
  status && Object.prototype.hasOwnProperty.call(STATUS_BADGE, status)
    ? STATUS_BADGE[status as TranslationStatus]
    : null

/** Флаг + имя языка — был продублирован в `SourceLangSelect.LANGS` и
 *  `TranslationLangRow.INFO` (вкладка «Язык», Task 8). Одно место на все три
 *  компонента, которые его читают (`SourceLangSelect`, `TranslationLangRow`,
 *  `LanguagesSection`). */
export const LANG_INFO: Record<Lang, { flag: string; name: string }> = {
  ru: { flag: '🇷🇺', name: 'Русский' },
  en: { flag: '🇬🇧', name: 'English' },
}
